import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Application } from './admin/types/application';
import {
  Box, Typography, Divider, Chip, Button, CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, Checkbox, FormControlLabel, Snackbar, Alert, Card, useTheme, useMediaQuery,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DevicesIcon from '@mui/icons-material/Devices';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PersonIcon from '@mui/icons-material/Person';
import { motion } from 'framer-motion';
import STATUS_COLORS from '../constants/statusColors';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';


const bgGradient = 'linear-gradient(135deg, #e0f2fe 0%, #f8fafc 55%, #e0e7ff 100%)';

const ApplicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showFullAccount, setShowFullAccount] = useState(false);
  
  // 택배송장 관련 상태
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingType, setTrackingType] = useState<'delivery' | 'return'>('delivery');
  const [trackingSubmitting, setTrackingSubmitting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');


  useEffect(() => {
    if (!id) return;
    const fetchApplication = async () => {
      const ref = doc(db, 'applications', id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() as Application;
        setApplication({ ...data, id: snap.id });
      }
      setLoading(false);
    };
    fetchApplication();
  }, [id]);

  const handleAccept = async () => {
    if (!id || !agree) return;
    setSubmitting(true);
    const ref = doc(db, 'applications', id);
    await updateDoc(ref, {
      status: '입금대기',
      price_accepted: true,
      price_accepted_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
    setSnackbarMessage('입금 요청이 완료되었습니다.');
    setSnackbarSeverity('success');
    setShowSnackbar(true);
    setTimeout(() => navigate('/my-list'), 1500);
  };

  // 택배송장 다이얼로그 열기
  const handleOpenTrackingDialog = (type: 'delivery' | 'return') => {
    setTrackingType(type);
    setTrackingNumber('');
    setTrackingDialogOpen(true);
  };

  // 택배송장 저장
  const handleSaveTracking = async () => {
    if (!id || !trackingNumber.trim()) return;
    setTrackingSubmitting(true);
    
    try {
      const ref = doc(db, 'applications', id);
      const updateData: any = {
        updated_at: serverTimestamp(),
      };
      
      if (trackingType === 'delivery') {
        updateData.tracking_number = trackingNumber.trim();
      } else {
        updateData.return_tracking_number = trackingNumber.trim();
      }
      
      await updateDoc(ref, updateData);
      
      // 상태 업데이트
      setApplication(prev => prev ? {
        ...prev,
        ...(trackingType === 'delivery' 
          ? { tracking_number: trackingNumber.trim() }
          : { return_tracking_number: trackingNumber.trim() }
        )
      } : null);
      
      setSnackbarMessage(`${trackingType === 'delivery' ? '택배송장' : '반품 택배송장'}번호가 저장되었습니다.`);
      setSnackbarSeverity('success');
      setShowSnackbar(true);
      setTrackingDialogOpen(false);
    } catch (error) {
      console.error('택배송장 저장 실패:', error);
      setSnackbarMessage('택배송장번호 저장에 실패했습니다.');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    } finally {
      setTrackingSubmitting(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ textAlign: 'center', mt: 8, minHeight: '60vh', bgcolor: bgGradient }}>
        <CircularProgress />
      </Box>
    );
  if (!application)
    return (
      <Box sx={{ textAlign: 'center', mt: 8, minHeight: '60vh', bgcolor: bgGradient }}>
        <Typography align="center">신청 정보를 찾을 수 없습니다.</Typography>
      </Box>
    );

  // 계좌번호 마스킹: ex) 3333-01-****-1234
  const maskAccountNumber = (acc: string) => {
    if (!acc) return '';
    // 뒤 4자리를 제외하고 * 처리
    const match = acc.match(/^(\d{2,4}[- ]?\d{2,4}[- ]?)?([\d\- ]+)?(\d{4})$/);
    if (match) {
      const pre = acc.slice(0, -4).replace(/\d/g, '*');
      return pre + acc.slice(-4);
    }
    // 그냥 뒤 4자리 빼고 마스킹
    return acc.length > 4 ? '*'.repeat(acc.length - 4) + acc.slice(-4) : acc;
  };

  return (
    <Box
      sx={{
        width: '100vw',
        minHeight: '100vh',
        background: bgGradient,
        position: 'relative',
        py: { xs: 3, md: 8 },
        px: 0,
        overflow: 'hidden'
      }}
    >
      {/* floating blur 배경 */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 0.15, scale: 1 }}
        transition={{ duration: 1.3 }}
        sx={{
          position: 'absolute',
          top: { xs: '-55px', md: '-90px' },
          left: { xs: '-70px', md: '-110px' },
          width: { xs: 130, md: 210 },
          height: { xs: 130, md: 210 },
          background: 'linear-gradient(135deg, #e0e7ff 0%, #dbeafe 100%)',
          filter: 'blur(65px)',
          borderRadius: '50%',
          zIndex: 0,
        }}
      />
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 90 }}
        animate={{ opacity: 0.11, y: 0 }}
        transition={{ duration: 1.5 }}
        sx={{
          position: 'absolute',
          bottom: { xs: '-60px', md: '-120px' },
          right: { xs: '-65px', md: '-120px' },
          width: { xs: 120, md: 200 },
          height: { xs: 120, md: 200 },
          background: 'linear-gradient(135deg, #a5b4fc 0%, #f0abfc 100%)',
          filter: 'blur(70px)',
          borderRadius: '50%',
          zIndex: 0,
        }}
      />

      {/* 중앙 카드 Wrapper */}
      <Box
        sx={{
          maxWidth: 800,
          mx: 'auto',
          px: { xs: 1.5, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 3, md: 4 }
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon sx={{ fontSize: 26 }} />}
          sx={{
            mb: 1.5,
            alignSelf: 'flex-start',
            fontWeight: 700,
            borderRadius: 99,
            background: 'linear-gradient(90deg, #0ea5e9 0%, #8b5cf6 100%)',
            color: '#fff',
            fontSize: { xs: 15, md: 17 },
            px: { xs: 2, md: 3.2 },
            py: { xs: 1, md: 1.2 },
            minHeight: 44,
            minWidth: 0,
            textTransform: 'none',
            boxShadow: '0 2px 16px rgba(14,165,233,0.10)',
            transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
            '&:hover': {
              background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
              boxShadow: '0 6px 20px 0 rgba(139,92,246,0.13)',
              filter: 'brightness(1.07)'
            }
          }}
          component={motion.button}
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.025 }}
        >
          돌아가기
        </Button>

        {/* 상세 정보 카드 */}
        <Card
          component={motion.div}
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          variant="outlined"
          sx={{
            width: '100%',
            p: isMobile ? 2 : 3.5,
            borderRadius: 5,
            background: 'rgba(255,255,255,0.94)',
            backdropFilter: 'blur(13px)',
            boxShadow: '0 4px 26px 0 rgba(43,64,176,0.10)',
            border: '1.5px solid #e0e7ef',
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight="bold"
            gutterBottom
          >
            📄 신청 상세 정보
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" gap={1}><PersonIcon fontSize="small" sx={{ color: '#6366f1' }} /><Typography fontWeight={700}><strong>이름:</strong> {application.user_name}</Typography></Box>
            <Box display="flex" alignItems="center" gap={1}><PhoneIphoneIcon fontSize="small" sx={{ color: '#0ea5e9' }} /><Typography fontWeight={700}><strong>전화번호:</strong> {application.phone_number}</Typography></Box>
            <Box display="flex" alignItems="center" gap={1}><HomeIcon fontSize="small" sx={{ color: '#3b82f6' }} /><Typography fontWeight={700}><strong>주소:</strong> {application.pickup_address} {application.pickup_address_detail}</Typography></Box>
            <Typography fontWeight={600}><strong>신청일:</strong> {application.created_at?.seconds ? new Date(application.created_at.seconds * 1000).toLocaleString() : '-'}</Typography>
            <Typography fontWeight={700} display="flex" alignItems="center">
              <strong>상태:</strong>
              <Chip
                label={application.status}
                size="small"
                sx={{
                  ml: 1,
                  fontWeight: 700,
                  borderRadius: 2.5,
                  fontSize: 14,
                  px: 1.2,
                  color: '#fff',
                  background: STATUS_COLORS[application.status] || '#a5b4fc',
                  boxShadow: `0 2px 10px 0 ${(STATUS_COLORS[application.status] || '#555')}1c`
                }}
              />
            </Typography>
            
            {/* 택배송장 정보 */}
            <Box sx={{ mt: 2 }}>
              <Typography fontWeight={700} sx={{ mb: 1.5, color: '#374151' }}>📦 택배송장 정보</Typography>
              
              {/* 일반 택배송장 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography fontWeight={600} sx={{ minWidth: 100 }}>발송 송장:</Typography>
                {application.tracking_number ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Chip
                      label={application.tracking_number}
                      variant="outlined"
                      sx={{ fontWeight: 600, bgcolor: '#f0f9ff', color: '#1565c0' }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => window.open(`https://tracker.delivery/#/kr.cjlogistics/${application.tracking_number}`, '_blank')}
                      sx={{
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        borderColor: '#0ea5e9',
                        color: '#0ea5e9',
                        '&:hover': {
                          borderColor: '#0284c7',
                          color: '#0284c7',
                          bgcolor: 'rgba(14, 165, 233, 0.05)',
                        }
                      }}
                    >
                      배송조회
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleOpenTrackingDialog('delivery')}
                      sx={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        minWidth: 'auto',
                        px: 1,
                        '&:hover': { bgcolor: 'rgba(107, 114, 128, 0.05)' }
                      }}
                    >
                      수정
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenTrackingDialog('delivery')}
                    sx={{
                      fontSize: '0.875rem',
                      borderRadius: 2,
                      px: 2,
                      py: 0.8,
                      borderColor: '#6366f1',
                      color: '#6366f1',
                      '&:hover': {
                        borderColor: '#5b21b6',
                        color: '#5b21b6',
                        bgcolor: 'rgba(99, 102, 241, 0.05)',
                      }
                    }}
                  >
                    + 택배송장 입력
                  </Button>
                )}
              </Box>

                        {/* 반품 택배송장 (반송 상태일 때만 표시) */}
          {(application.status === '반송' || (application as any).return_tracking_number) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography fontWeight={600} sx={{ minWidth: 100 }}>반품 송장:</Typography>
                  {(application as any).return_tracking_number ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Chip
                        label={(application as any).return_tracking_number}
                        variant="outlined"
                        sx={{ fontWeight: 600, bgcolor: '#fef2f2', color: '#dc2626' }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => window.open(`https://tracker.delivery/#/kr.cjlogistics/${(application as any).return_tracking_number}`, '_blank')}
                        sx={{
                          fontSize: '0.75rem',
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5,
                          minWidth: 'auto',
                          borderColor: '#ef4444',
                          color: '#ef4444',
                          '&:hover': {
                            borderColor: '#dc2626',
                            color: '#dc2626',
                            bgcolor: 'rgba(239, 68, 68, 0.05)',
                          }
                        }}
                      >
                        배송조회
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleOpenTrackingDialog('return')}
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6b7280',
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': { bgcolor: 'rgba(107, 114, 128, 0.05)' }
                        }}
                      >
                        수정
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenTrackingDialog('return')}
                      sx={{
                        fontSize: '0.875rem',
                        borderRadius: 2,
                        px: 2,
                        py: 0.8,
                        borderColor: '#ef4444',
                        color: '#ef4444',
                        '&:hover': {
                          borderColor: '#dc2626',
                          color: '#dc2626',
                          bgcolor: 'rgba(239, 68, 68, 0.05)',
                        }
                      }}
                    >
                      + 반품송장 입력
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Card>

        {/* 기기 정보 */}
        <Card
          component={motion.div}
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.82 }}
          variant="outlined"
          sx={{
            width: '100%',
            p: isMobile ? 2 : 3.2,
            borderRadius: 5,
            background: 'rgba(255,255,255,0.91)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 22px 0 rgba(43,64,176,0.07)',
            border: '1.5px solid #e0e7ef',
          }}
        >
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" gutterBottom>
            📱 기기 정보
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ borderRadius: 3, overflow: 'hidden', minWidth: isMobile ? 600 : 'auto' }}>
              <TableHead>
                <TableRow sx={{ background: 'rgba(232,244,255,0.69)' }}>
                  <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>기기 종류</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: isMobile ? 140 : 160 }}>모델명</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', minWidth: 80 }}>등급</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right', minWidth: 100 }}>제안금액</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right', minWidth: 100 }}>최종금액</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {application.devices.map((device, index) => (
                  <TableRow key={device.id || index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(248, 250, 252, 0.5)' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>{device.deviceType}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      <Box component="span" sx={{ 
                        display: 'block', 
                        maxWidth: isMobile ? 120 : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobile ? 'nowrap' : 'normal'
                      }}>
                        {device.deviceModel}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {device.grade ? (
                        <Chip
                          label={device.grade}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: 24,
                            bgcolor: 
                              device.grade === 'A' ? '#dcfce7' :
                              device.grade === 'B' ? '#dbeafe' :
                              device.grade === 'C' ? '#fef3c7' :
                              device.grade === 'D' ? '#fee2e2' : '#f3f4f6',
                            color:
                              device.grade === 'A' ? '#15803d' :
                              device.grade === 'B' ? '#1d4ed8' :
                              device.grade === 'C' ? '#d97706' :
                              device.grade === 'D' ? '#dc2626' : '#6b7280',
                            border: 'none'
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                          미정
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                      {device.price_offer ? (
                        <Typography variant="body2" sx={{ 
                          color: '#374151',
                          fontWeight: 700,
                          fontSize: '0.875rem'
                        }}>
                          {device.price_offer.toLocaleString()}원
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                          미정
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                      {device.final_price ? (
                        <Typography variant="body2" sx={{ 
                          color: '#059669',
                          fontWeight: 700,
                          fontSize: '0.875rem'
                        }}>
                          {device.final_price.toLocaleString()}원
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                          미정
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          
          {/* 기기별 차감/반송 사유 표시 */}
          {application.devices.some(device => device.deduction_reason || device.reject_reason) && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(229, 231, 235, 0.8)' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: '#374151' }}>
                📋 기기별 특이사항
              </Typography>
              {application.devices.map((device, index) => (
                (device.deduction_reason || device.reject_reason) && (
                  <Box key={device.id || index} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                      {device.deviceModel}
                    </Typography>
                    {device.deduction_reason && (
                      <Typography variant="body2" sx={{ 
                        color: '#d97706', 
                        fontSize: '0.875rem',
                        bgcolor: 'rgba(251, 191, 36, 0.1)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'inline-block',
                        mb: 0.5
                      }}>
                        💰 차감사유: {device.deduction_reason}
                      </Typography>
                    )}
                    {device.reject_reason && (
                      <Typography variant="body2" sx={{ 
                        color: '#dc2626', 
                        fontSize: '0.875rem',
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'inline-block'
                      }}>
                        ❌ 반송사유: {device.reject_reason}
                      </Typography>
                    )}
                  </Box>
                )
              ))}
            </Box>
          )}
        </Card>

        {/* 금액/계좌 정보 */}
        <Card
          component={motion.div}
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.92 }}
          variant="outlined"
          sx={{
            width: '100%',
            p: isMobile ? 2 : 3.2,
            borderRadius: 5,
            background: 'rgba(255,255,255,0.88)',
            backdropFilter: 'blur(7px)',
            boxShadow: '0 4px 24px 0 rgba(43,64,176,0.09)',
            border: '1.5px solid #dde3f7'
          }}
        >
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" gutterBottom>
            💰 금액/계좌 정보
          </Typography>
          <Divider sx={{ my: 2 }} />

          {/* 상태에 따른 적절한 금액 표시 */}
          {(() => {
            // 새로운 필드들 우선 사용, 없으면 기존 필드 사용
            const totalPriceOffer = application.total_price_offer || application.price_offer;
            const totalFinalPrice = application.total_final_price || application.final_price;
            
            // 상태에 따른 금액 표시 로직
            if (application.status === '완료' && totalFinalPrice) {
              return (
                <Typography
                  variant={isMobile ? "h6" : "h4"}
                  fontWeight="bold"
                  sx={{
                    mb: 2,
                    background: 'linear-gradient(90deg, #059669 0%, #0ea5e9 100%)',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    letterSpacing: 0.5
                  }}
                >
                  확정 금액: {totalFinalPrice.toLocaleString()}원
                </Typography>
              );
            } else if (application.price_accepted && totalFinalPrice) {
              return (
                <Typography
                  variant={isMobile ? "h6" : "h4"}
                  fontWeight="bold"
                  sx={{
                    mb: 2,
                    background: 'linear-gradient(90deg,#0ea5e9,#3b82f6,#8b5cf6)',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    letterSpacing: 0.5
                  }}
                >
                  최종 금액: {totalFinalPrice.toLocaleString()}원
                </Typography>
              );
            } else if (totalPriceOffer) {
              return (
                <Typography
                  variant={isMobile ? "h6" : "h4"}
                  fontWeight="bold"
                  sx={{
                    mb: 2,
                    background: 'linear-gradient(90deg, #3b82f6 0%, #0ea5e9 100%)',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    letterSpacing: 0.5
                  }}
                >
                  제안 금액: {totalPriceOffer.toLocaleString()}원
                </Typography>
              );
            }
            return null;
          })()}
          
          {/* 기기별 금액 합계 표시 (디버깅/확인용) */}
          {application.devices && application.devices.length > 1 && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: 'rgba(248, 250, 252, 0.8)', 
              borderRadius: 2,
              border: '1px solid rgba(229, 231, 235, 0.5)'
            }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#374151' }}>
                📊 기기별 금액 합계
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {(() => {
                  const deviceTotalOffer = application.devices.reduce((sum, device) => sum + (device.price_offer || 0), 0);
                  const deviceTotalFinal = application.devices.reduce((sum, device) => sum + (device.final_price || 0), 0);
                  
                  return (
                    <>
                      {deviceTotalOffer > 0 && (
                        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          제안금액 합계: <strong style={{ color: '#374151' }}>{deviceTotalOffer.toLocaleString()}원</strong>
                        </Typography>
                      )}
                      {deviceTotalFinal > 0 && (
                        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          최종금액 합계: <strong style={{ color: '#059669' }}>{deviceTotalFinal.toLocaleString()}원</strong>
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Box>
            </Box>
          )}

          {/* 계좌정보는 항상 노출 */}
          <Divider sx={{ my: 2 }} />
          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            <Box display="flex" alignItems="center" gap={1.3}>
              <Typography fontWeight="bold">🏦 은행명:</Typography>
              <Chip
                label={application.bank_name || "-"}
                variant="outlined"
                sx={{ fontWeight: 700, borderRadius: 2, bgcolor: '#f1f5fd' }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1.3}>
              <Typography fontWeight="bold">🔢 계좌번호:</Typography>
              <Chip
                label={
                  showFullAccount
                    ? application.bank_account || "-"
                    : maskAccountNumber(application.bank_account || "")
                }
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  borderRadius: 2,
                  bgcolor: '#f1f5fd',
                  letterSpacing: 2,
                  fontSize: 15
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowFullAccount(v => !v)}
                startIcon={
                  showFullAccount
                    ? <VisibilityOff sx={{ fontSize: 18, transition: 'all 0.3s ease' }} />
                    : <Visibility sx={{ fontSize: 18, transition: 'all 0.3s ease' }} />
                }
                sx={{
                  borderRadius: 16,
                  ml: 1,
                  px: 2.5,
                  py: 0.8,
                  fontWeight: 600,
                  fontSize: 13,
                  minWidth: 'auto',
                  textTransform: 'none',
                  border: 'none',
                  position: 'relative',
                  overflow: 'hidden',

                  // 현대적인 글래스모피즘 효과
                  background: showFullAccount
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',

                  backdropFilter: 'blur(10px)',
                  boxShadow: showFullAccount
                    ? '0 8px 32px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 8px 32px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',

                  color: showFullAccount ? '#6366f1' : '#3b82f6',

                  transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',

                  // 호버 효과
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    background: showFullAccount
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',

                    boxShadow: showFullAccount
                      ? '0 12px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                      : '0 12px 40px rgba(59, 130, 246, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.3)',

                    color: showFullAccount ? '#5b21b6' : '#1e40af',
                    border: 'none',
                  },

                  // 액티브 상태
                  '&:active': {
                    transform: 'translateY(0px)',
                    transition: 'all 0.1s ease',
                  },

                  // 포커스 상태
                  '&:focus': {
                    outline: 'none',
                    boxShadow: showFullAccount
                      ? '0 0 0 3px rgba(99, 102, 241, 0.2), 0 8px 32px rgba(99, 102, 241, 0.15)'
                      : '0 0 0 3px rgba(59, 130, 246, 0.2), 0 8px 32px rgba(59, 130, 246, 0.15)',
                  },

                  // 반짝이는 효과를 위한 가상 요소
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                    transition: 'left 0.6s ease',
                  },

                  '&:hover::before': {
                    left: '100%',
                  }
                }}
              >
                {showFullAccount ? '숨기기' : '모두보기'}
              </Button>
            </Box>
            <Box display="flex" alignItems="center" gap={1.3}>
              <Typography fontWeight="bold">👤 예금주:</Typography>
              <Chip
                label={application.bank_owner || application.user_name}
                variant="outlined"
                sx={{ fontWeight: 700, borderRadius: 2, bgcolor: '#f1f5fd' }}
              />
            </Box>
          </Box>


          {application.remark && (
            <Typography sx={{ mt: 2, whiteSpace: 'pre-line', color: '#334155', fontWeight: 600 }}>
              <strong>📌 비고:</strong> {application.remark}
            </Typography>
          )}
        </Card>

        {/* 가격안내 수락 (계좌정보 재입력 없이!) */}
        {application.status === '가격안내' && !application.price_accepted && (
          <Card
            component={motion.div}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.09 }}
            variant="outlined"
            sx={{
              width: '100%',
              p: isMobile ? 2 : 3,
              borderRadius: 5,
              background: 'rgba(255,255,255,0.91)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 24px 0 rgba(56,106,233,0.09)',
              border: '1.5px solid #dde3f7'
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              sx={{
                background: 'linear-gradient(90deg, #0ea5e9 0%, #6366f1 90%)',
                WebkitBackgroundClip: 'text',
                color: 'transparent'
              }}
            >입금 동의 및 요청</Typography>
            <Typography color="text.secondary" mb={2}>
              아래 계좌정보로 입금이 진행됩니다. 거래 조건에 동의하시면 "수락하고 입금 요청하기"를 눌러주세요.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              }
              label="거래 조건(계좌정보/금액)에 동의합니다."
              sx={{ mb: 2, ml: 0.5 }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!agree || submitting}
              onClick={handleAccept}
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? 15 : 17,
                borderRadius: 99,
                px: 5, py: 1.7,
                background: 'linear-gradient(90deg, #0ea5e9 0%, #8b5cf6 100%)',
                boxShadow: '0 2px 12px 0 rgba(139,92,246,0.13)',
                textTransform: 'none',
                transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
                '&:hover': {
                  filter: 'brightness(1.10) saturate(1.1)',
                  background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                  boxShadow: '0 5px 17px 0 rgba(139,92,246,0.15)'
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(90deg, #e0e7ef 0%, #f1f5f9 100%) !important',
                  color: '#b0b4bb !important',
                  boxShadow: 'none',
                  opacity: 1,
                  cursor: 'not-allowed',
                }
              }}
              component={motion.button}
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: !submitting && agree ? 1.022 : 1 }}
            >
              수락하고 입금 요청하기
            </Button>
          </Card>
        )}
      </Box>

      {/* 택배송장 입력 다이얼로그 */}
      <Dialog 
        open={trackingDialogOpen} 
        onClose={() => setTrackingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: trackingType === 'delivery' 
              ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(14, 165, 233, 0.06) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(251, 146, 60, 0.06) 100%)',
            borderBottom: `1px solid ${trackingType === 'delivery' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(239, 68, 68, 0.1)'}`,
            fontWeight: 700,
            color: '#374151',
          }}
        >
          {trackingType === 'delivery' ? '📦 택배송장번호 입력' : '📦 반품송장번호 입력'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.6 }}>
            {trackingType === 'delivery' 
              ? '기기를 발송하신 택배송장번호를 입력해주세요. CJ대한통운 송장번호를 권장합니다.'
              : '반품받으신 택배송장번호를 입력해주세요.'}
          </Typography>
          <TextField
            autoFocus
            label={trackingType === 'delivery' ? '택배송장번호' : '반품송장번호'}
            fullWidth
            variant="outlined"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="예: 123456789012"
            helperText="숫자만 입력해주세요 (하이픈 제외)"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: trackingType === 'delivery' ? '#6366f1' : '#ef4444',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: trackingType === 'delivery' ? '#6366f1' : '#ef4444',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setTrackingDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1,
              borderColor: 'rgba(107, 114, 128, 0.3)',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#6b7280',
                bgcolor: 'rgba(107, 114, 128, 0.05)',
              }
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleSaveTracking}
            variant="contained"
            disabled={!trackingNumber.trim() || trackingSubmitting}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1,
              fontWeight: 700,
              background: trackingType === 'delivery' 
                ? 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
              boxShadow: trackingType === 'delivery'
                ? '0 4px 16px rgba(99, 102, 241, 0.3)'
                : '0 4px 16px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                background: trackingType === 'delivery'
                  ? 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)'
                  : 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
                boxShadow: trackingType === 'delivery'
                  ? '0 6px 20px rgba(99, 102, 241, 0.4)'
                  : '0 6px 20px rgba(239, 68, 68, 0.4)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%)',
                color: '#9ca3af',
              }
            }}
          >
            {trackingSubmitting ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={3000} 
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          sx={{ 
            width: '100%',
            borderRadius: 3,
            fontWeight: 600,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApplicationDetailPage;
